// Playing with using route loaders to speed up render.

import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams, type LoaderFunctionArgs } from "react-router";

import * as auth from "@skilitics/auth-client";
import getAuthClient from "@skilitics/designer-auth";
import { waitForLoginState } from "@skilitics/designer-auth/state";

import { getQueryClient } from "../../routing/context.ts";

const inviteOptions = (token: string) =>
  queryOptions({
    queryKey: ["invite", token],
    async queryFn() {
      try {
        return await getAuthClient().fetchInvite(token!);
      } catch (error) {
        // Don't show toasts if the invite is invalid (cancelled, expired etc.).
        // A little janky - this could also be that the route is wrong or something,
        // but that's REST for you.
        if (error instanceof auth.AuthApiError && error.code === "no-invite-for-token") {
          return null;
        }
        throw error;
      }
    },
  });

export async function loader({ context, params: { token } }: LoaderFunctionArgs) {
  // Don't cache a not authenticated error
  if ((await waitForLoginState()) !== "authenticated") {
    return null;
  }
  const queryClient = getQueryClient(context);
  await queryClient.prefetchQuery(inviteOptions(token!));
}

export const useTokenParam = () => useParams<{ token: string }>().token!;

export function useRouteInviteQuery(): UseQueryResult<auth.InviteInfo | null> {
  const token = useTokenParam();
  return useQuery(inviteOptions(token!));
}
